/**
 * 路由權限配置
 *
 * 定義每個路由需要的角色權限
 */
import {UserRole} from "../model/test";


// 路由到角色權限的映射表
interface RoutePermissions {
  [key: string]: UserRole[];
}

/**
 * 路由權限配置表
 * 
 * 每個路由對應一個角色數組，只有擁有該角色的用戶才能訪問該路由
 * UserRole: 1~5
 * 
 * 例如:
 * - 角色1可以訪問所有路由
 * - 角色2可以訪問會籍相關路由
 * - 角色3可以訪問產品相關路由
 * - 角色4可以訪問認證相關路由
 * - 角色5可以訪問其他路由
 */
export const ACCESS_PERMISSIONS: RoutePermissions = {
  '/': [1, 2, 3, 4], // 首頁所有角色都可以訪問
  
  // 會員檔案維護與查詢
  '/membership-master': [1, 2, 3, 4],
  '/membership/detail': [1, 2, 3, 4], // 會員詳情頁面
  '/product-master': [1, 2, 3, 4],
  '/certificate-master': [1, 2, 3, 4],
  
  // 其他
  '/price-certificate': [1, 2],
  '/member-grade-check': [1, 2],
  '/member-statistics': [1, 2, 3, 4],
  '/member-directory-export': [1, 2, 3],
  '/zongjiu-export': [1, 2, 3],
  
  // 專業委員會
  '/committee': [1, 2, 3, 4],
  
  // 活動維護
  '/association-activity': [1, 2, 3, 4],

  // 系統管理設定
  '/admin/users': [1],
  '/admin/permissions': [1],
  '/admin/parameters': [1],
};

/**
 * 菜單項到角色權限的映射表
 */
export const MENU_PERMISSIONS: RoutePermissions = {
  'membership-management': [1, 2, 3, 4], // 有子菜單任一權限就顯示父菜單
  'membership-master': [1, 2, 3, 4],
  'product-master': [1, 2, 3, 4],
  'certificate-master': [1, 2, 3, 4],
  
  'other': [1, 2, 3, 4],
  'price-certificate': [1, 2],
  'member-grade-check': [1, 2],
  'member-statistics': [1, 2, 3, 4],
  'member-directory-export': [1, 2, 3],
  'zongjiu-export': [1, 2, 3],
  
  'professional-committee': [1, 2, 3, 4],
  'committee': [1, 2, 3, 4],
  
  'activity-management': [1, 2, 3, 4],
  'association-activity': [1, 2, 3, 4],
  
  'system-administration': [1],
  'admin-users': [1],
  'admin-parameters': [1],
};

/**
 * 根據路徑獲取所需角色
 * 
 * @param path 當前路徑
 * @returns 所需角色數組
 */
export const getRequiredRoles = (path: string): UserRole[] => {
  // 精確匹配
  if (ACCESS_PERMISSIONS[path]) {
    return ACCESS_PERMISSIONS[path];
  }
  
  // 嘗試前綴匹配
  for (const route in ACCESS_PERMISSIONS) {
    if (path.startsWith(route) && route !== '/') {
      return ACCESS_PERMISSIONS[route];
    }
  }
  
  // 默認只有角色1可以訪問
  return [1];
};

/**
 * 檢查用戶是否有權限訪問該菜單項
 * 
 * @param menuKey 菜單項的key
 * @param userRole 用戶角色
 * @returns 是否有權限
 */
export const hasMenuPermission = (menuKey: string, userRole: UserRole): boolean => {
  if (!MENU_PERMISSIONS[menuKey]) {
    return false;
  }
  
  return MENU_PERMISSIONS[menuKey].includes(userRole);
};

/**
 * 檢查是否應該顯示某個菜單項
 * 
 * @param menuPath 菜單路徑
 * @param userRole 用戶角色
 * @returns 是否應該顯示
 */
export const shouldShowMenuItem = (menuPath: string, userRole: UserRole | undefined): boolean => {
  if (!userRole) return false;
  
  // 從路徑中提取菜單 key
  const menuKey = menuPath.startsWith('/') ? menuPath.substring(1) : menuPath;
  
  return hasMenuPermission(menuKey, userRole);
};

/**
 * 檢查是否應該顯示父菜單項
 * 
 * @param parentKey 父菜單的key
 * @param userRole 用戶角色
 * @returns 是否應該顯示
 */
export const shouldShowParentMenuItem = (parentKey: string, userRole: UserRole | undefined): boolean => {
  if (!userRole) return false;
  
  // 檢查父菜單的權限
  return hasMenuPermission(parentKey, userRole);
};
